import { Layouts } from '../controls/layouts';
import { Buttons } from '../controls/buttons';
import { Component, computed, effect, inject, output, resource, signal, ChangeDetectionStrategy } from '@angular/core';
import { PartnerComponentBase } from './partner-search-details.base';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PartnerOnboardingService } from './services/partner-onboarding.service';
import { CurrentUserService } from '../user/services';
import { ConversationData, CredentialsInformationItem, FileInformationItem, ItemUpdatedEvent, PartnerOnboardingInformationItemComponent, PasswordInformationItem, TextInformationItem } from './onboarding-details/partner-onboarding-information-item/partner-onboarding-information-item.component';
import { CreatedEntity, CredentialsStatus, DocumentDirection, DocumentTypeOutput, FormPartnerConversation, Identification, ItemStatus, NewCommentEvent, PartnerContact, PartnerConversation, PartnerName, PartnerOnboarding, PartnerOnboardingComment, PartnerOnboardingData, PartnerOnboardingStatus, ToggleConversationEvent, Transport, TransportField, TransportFieldOwner, TransportFieldType, TransportType } from '../partnerportal/models/partner-onboarding';
import { GenericModalService, ModalSettingsTemplate } from '../shared/generic-modal/generic-modal.service';
import { PartnerOnboardingAcceptDialogComponent, PartnerOnboardingAcceptDialogResult } from './onboarding-details/partner-onboarding-accept-dialog/partner-onboarding-accept-dialog.component';
import { DeveloperService } from '../developer/services/developer.service';
import { DocumentConfig } from '../developer/models/document-config';
import { PartnerOnboardingAcceptDocumenttypeDialogComponent, PartnerOnboardingAcceptDocumentTypeDialogResult, PartnerOnboardingAcceptDocumentTypeResult } from './onboarding-details/partner-onboarding-accept-documenttype-dialog/partner-onboarding-accept-documenttype-dialog.component';
import { Router } from '@angular/router';
import { filter, interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PartnerOnboardingInformationGroupComponent } from './onboarding-details/partner-onboarding-information-group/partner-onboarding-information-group.component';
import { PartnerOnboardingReopenDialogComponent, PartnerOnboardingReopenDialogResult, ReopenType } from './onboarding-details/partner-onboarding-reopen-dialog/partner-onboarding-reopen-dialog.component';
import { LinkComponent } from '../controls/components/basic/link/link.component';
import { PartnerOnboardingLogEvent } from './models/partner-onboarding-logevent';
import { PartnerTagsService } from './services/partner-tags.service';
import { PartnerOnboardingAcceptContactDialogComponent, PartnerOnboardingAcceptContactDialogResult, PartnerOnboardingAcceptContactResult } from './onboarding-details/partner-onboarding-accept-contact-dialog/partner-onboarding-accept-contact-dialog.component';
import { GroupBoxComponent } from './onboarding-setup/partner-onboarding-transport/components/group-box/group-box.component';
import { OnboardingCredentials } from '../partnerportal/components/onboarding-credentials/models/credentials';
import { PasswordComponentValue } from '../controls/password';
import { OnboardingFile } from '../partnerportal/components/onboarding-file-selector/models/onboarding-file';
import { PartnerOnboardingProvideCredentialsDialogComponent, PartnerOnboardingProvideCredentialsDialogResult, PartnerOnboardingProvideCredentialsResult } from './onboarding-details/partner-onboarding-provide-credentials-dialog/partner-onboarding-provide-credentials-dialog.component';
import { ProductFruitsService } from '../shared/product-fruits.service';

@Component({
    templateUrl: './partner-search-details-onboarding-details.component.html',
    styleUrl: './partner-search-details-onboarding-details.component.scss',
    selector: 'ep-partner-details-onboarding-details',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ReactiveFormsModule, Layouts, PartnerOnboardingInformationItemComponent, PartnerOnboardingInformationGroupComponent, LinkComponent, Buttons, GroupBoxComponent]
})

export class PartnerSearchDetailsOnboardingDetailsComponent extends PartnerComponentBase {
  public partnerId = signal<number>(null);
  public changeTab = output<string>();
  public refresh = output();
  public docConfigs: DocumentConfig[] = [];
  public autoRefreshConversations = signal<boolean>(false);

  loadingPanelService = inject(LoadingPanelService);
  partnerOnboardingService = inject(PartnerOnboardingService);
  currentUserService = inject(CurrentUserService);
  modalService = inject(GenericModalService);
  developerService = inject(DeveloperService);
  partnerTagsService = inject(PartnerTagsService);
  router = inject(Router);
    productfruitsService = inject(ProductFruitsService);

  DocumentDirection = DocumentDirection;

  constructor() {
    super();
    effect(() => {
      if (this.partnerOnboardingResource.isLoading()) {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
      } else {
        this.loadingPanelService.hideLoadingPanel();
      }
    });

    interval(5000)
      .pipe(takeUntilDestroyed(), filter(() => this.autoRefreshConversations()))
      .subscribe(() => {
        this.conversationResource?.reload();
      });
  }

  async partnerSet(): Promise<unknown> {
    this.partnerId.set(this.selectedPartner?.PartnersId);
    this.partnerOnboardingResource.reload();
    return Promise.resolve();
  }


  // Resources
  partnerOnboardingResource = resource({
    params: () => ({partnerId: this.partnerId()}),
    loader: ({params}) => this.getOnboardingPartner(params),
  });

  documentConfigInResource = resource({
    params: () => ({formatId: 0, direction: 'in'}),
    loader: ({params}) => this.getDocumentConfigs(params),
  });

  internalPartnerIdResource = resource({
    params: () => ({}),
    loader: () => this.partnerOnboardingService.getInternalPartnerId(),
  });

  conversationResource = resource({
    params: () => ({partnerOnboardingId: this.partnerOnboarding()?.Id}),
    loader: ({params}) => this.getOnboardingConversations(params)
  });


  // Get methods for resources
  async getOnboardingPartner(request: {partnerId: number}): Promise<PartnerOnboarding> {
    if (request.partnerId) {
        const onboardingPartner = await this.partnerOnboardingService.getPartnerOnboarding(request.partnerId);
        return onboardingPartner;
    }
    else {
        return null;
    }
  }

  async getDocumentConfigs(request: {formatId: number, direction: string}): Promise<DocumentConfig[]> {
    if (request.formatId) {
      const configs = await this.developerService.getDocumentConfigs();
      return configs.filter(config => config.FormatId === request.formatId && config.Direction.toLocaleLowerCase() === request.direction.toLowerCase());
    }
    else {
      return [];
    }
  }

  async getOnboardingConversations(request: {partnerOnboardingId: number}): Promise<PartnerConversation[]> {
    if (request.partnerOnboardingId) {
      const conversations = await this.partnerOnboardingService.getConversations(request.partnerOnboardingId);

      // Find the visible conversation and mark it as read if it has unread comments
      const vc = conversations.find(
        c => c.Id === this.vissibleConversationId()?.Id
      );
      if (vc && this.hasUnreadComments(vc)) {
        vc.CustomerLastRead = new Date();
        void this.partnerOnboardingService.setConversationReadByCustomer(this.vissibleConversationId()?.Id);
      }
      return conversations;
    }
    else {
      return [];
    }
  }


  // Computed signals for resources
  internalPartnerId = computed(() => this.internalPartnerIdResource.value() );
  partnerOnboarding = computed<PartnerOnboarding>(() => this.partnerOnboardingResource.value() );
  partnerOnboardingData = computed<PartnerOnboardingData>(() => this.partnerOnboarding()?.Data );
  model = computed(() => this.partnerOnboardingData()?.Output);
  setup = computed(() => this.partnerOnboardingData()?.Input);
  status = computed(() => this.partnerOnboarding()?.Status ?? PartnerOnboardingStatus.Unknown);
  conversations = computed<PartnerConversation[]>(() => this.conversationResource.value() );
  vissibleConversationId = signal<VissibleConversation | null>(null);
  visibleConversation = computed<FormPartnerConversation>(() => {
    if (!this.vissibleConversationId()) {
      return null;
    }

    const conversationId = this.vissibleConversationId()?.Id;
    const areaId = this.vissibleConversationId()?.AreaId;

    const conversations = this.conversations()?.filter(
      (c) => c.Id === conversationId
    );

    if (conversations?.length >= 1) {
      const conversation = conversations[0];
      return new FormPartnerConversation(conversation, areaId);
    } else {
      return new FormPartnerConversation(
          new PartnerConversation({
            Id: conversationId,
              Comments: [],
              PartnerLastRead: null,
              CustomerLastRead: null,
          }),
          areaId
      );
    }
  });


  // Signals for status text
  unreadConversationIds = computed(() => this.conversations()?.filter(c => this.hasUnreadComments(c)).map(c => c.Id) ?? []);

  elementsAwaitingAcceptance = computed(() => {
    const elements: unknown[] = [];
    if (this.model()) {
      if (this.model().Name.Status.Status === ItemStatus.AwaitingAcceptance) {
        elements.push(this.model().Name);
      }
      if (this.model().Contact.Name.Status.Status === ItemStatus.AwaitingAcceptance) {
        elements.push(this.model().Contact.Name);
      }
      if (this.model().Contact.Email.Status.Status === ItemStatus.AwaitingAcceptance) {
        elements.push(this.model().Contact.Email);
      }
      if (this.model().Contact.PhoneNumber.Status.Status === ItemStatus.AwaitingAcceptance) {
        elements.push(this.model().Contact.PhoneNumber);
      }
      this.model().Identifications?.filter(i => i.Status.Status === ItemStatus.AwaitingAcceptance).forEach(i => elements.push(i));
      this.model().DocumentTypesIn?.filter(d => d.Status.Status === ItemStatus.AwaitingAcceptance).forEach(d => elements.push(d));
      this.model().DocumentTypesOut?.filter(d => d.Status.Status === ItemStatus.AwaitingAcceptance).forEach(d => elements.push(d));
    }
    return elements;
  });

  elementsAwaitingPartnerInput = computed(() => {
    const elements: unknown[] = [];
    if (this.model()) {
      if (this.model().Name.Status.Status === ItemStatus.Open) {
        elements.push(this.model().Name);
      }
      if (this.model().Contact.Name.Status.Status === ItemStatus.Open) {
        elements.push(this.model().Contact.Name);
      }
      if (this.model().Contact.Email.Status.Status === ItemStatus.Open) {
        elements.push(this.model().Contact.Email);
      }
      if (this.model().Contact.PhoneNumber.Status.Status === ItemStatus.Open) {
        elements.push(this.model().Contact.PhoneNumber);
      }
      this.model().Identifications?.filter(i => i.Status.Status === ItemStatus.Open).forEach(i => elements.push(i));
      this.model().DocumentTypesIn?.filter(d => d.Status.Status === ItemStatus.Open).forEach(d => elements.push(d));
      this.model().DocumentTypesOut?.filter(d => d.Status.Status === ItemStatus.Open).forEach(d => elements.push(d));
    }
    return elements;
  });

  elementsWithCredentialsRequested = computed(() => {
    const elements: unknown[] = [];
    if (this.setup()?.SupportedTransports) {
      this.setup().SupportedTransports?.filter(t => t.CredentialsStatus === CredentialsStatus.Requested).forEach(t => elements.push(t));
    }
    return elements;
  });

  statusIconClass: string = '';
  statusText = computed(() => {
    if (this.unreadConversationIds()?.length > 0) {
      this.statusIconClass = 'dx-icon-info unread-comments';
      return `You have ${this.unreadConversationIds()?.length} unread ${this.unreadConversationIds()?.length > 1 ? 'messages' : 'message'}`;
    }

    if (this.elementsWithCredentialsRequested()?.length > 0) {
      this.statusIconClass = 'dx-icon-info credentials-requested';
      return `You have ${this.elementsWithCredentialsRequested()?.length} pending credentials request${this.elementsWithCredentialsRequested()?.length > 1 ? 's' : ''}`;
    }

    if (this.status() === PartnerOnboardingStatus.InProgress) {
      this.statusIconClass = 'dx-icon-info in-progress';
      return 'Waiting for the partner to submit the onboarding.';
    }
    else if (this.status() === PartnerOnboardingStatus.PartnerDone) {
      if (this.elementsAwaitingAcceptance()?.length > 0) {
        this.statusIconClass = 'dx-icon-info awaiting-acceptance';
        return `You have ${this.elementsAwaitingAcceptance()?.length} item${this.elementsAwaitingAcceptance()?.length > 1 ? 's' : ''} awaiting your acceptance`;
      }
      else if (this.elementsAwaitingPartnerInput()?.length > 0) {
        this.statusIconClass = 'dx-icon-info awaiting-partner-input';
        return `You have ${this.elementsAwaitingPartnerInput()?.length} item${this.elementsAwaitingPartnerInput()?.length > 1 ? 's' : ''} awaiting input from the partner`;
      }
      else {
        this.statusIconClass = 'dx-icon-info all-accepted';
        return 'You have accepted all information provided by the partner.';
      }
    }
    else if (this.status() === PartnerOnboardingStatus.Done) {
      this.statusIconClass = 'dx-icon-check all-accepted';
      return 'Onboarding completed.';
    }
    else if (this.status() === PartnerOnboardingStatus.Unknown) {
      this.statusIconClass = 'dx-icon-info no-onboarding';
      return 'No onboarding information available.';
    }

    return '';
  });

  statusNumber = computed(() => {
    switch (this.status()) {
      case PartnerOnboardingStatus.InProgress:
        return 1;
      case PartnerOnboardingStatus.PartnerDone:
        if (this.elementsAwaitingAcceptance()?.length > 0 || this.elementsAwaitingPartnerInput()?.length > 0) {
          return 2;
        }
        else {
          return 3;
        }
      case PartnerOnboardingStatus.Done:
        return 4;
      default:
        return 0;
    }
  });

  canMarkOnboardingAsDone = computed(() => {
    if (
      this.status() === PartnerOnboardingStatus.PartnerDone &&
      this.unreadConversationIds()?.length === 0 &&
      this.elementsAwaitingAcceptance()?.length === 0 &&
      this.elementsAwaitingPartnerInput()?.length === 0) { // &&
      //this.elementsWithCredentialsRequested()?.length === 0) {
      return true;
    }
    return false;
  });

  canReopenOnboarding = computed(() => this.status() === PartnerOnboardingStatus.Done);
  

  // Update the transport fields in the document types with the merged list of transport fields from the setup to make sure all transport fields are displayed in the details, even if they are not filled by the partner
  mergedDocumentTypesIn = computed(() => {
    const mergedDocumentTypes: DocumentTypeOutput[] = [];
    this.model()?.DocumentTypesIn?.forEach(documentType => {
      if (!documentType.Transport) {
        mergedDocumentTypes.push(documentType);
        return;
      }
      const matchedSetupTransport = this.setup()?.SupportedTransports?.find(t => t.Type === documentType.Transport?.Type);
      const mergedTransportFields = this.mergeTransportFields(documentType.Transport?.TransportFields, matchedSetupTransport?.TransportFields);
      mergedDocumentTypes.push({
        ...documentType,
        Transport: new Transport({
          ...documentType.Transport,
          TransportFields: mergedTransportFields,
          CredentialsStatus: matchedSetupTransport?.CredentialsStatus
        })
      });
    });
    return mergedDocumentTypes;
  });

  mergedDocumentTypesOut = computed(() => {
    const mergedDocumentTypes: DocumentTypeOutput[] = [];
    this.model()?.DocumentTypesOut?.forEach(documentType => {
      if (!documentType.Transport) {
        mergedDocumentTypes.push(documentType);
        return;
      }
      const matchedSetupTransport = this.setup()?.SupportedTransports?.find(t => t.Type === documentType.Transport?.Type);
      const mergedTransportFields = this.mergeTransportFields(documentType.Transport?.TransportFields, matchedSetupTransport?.TransportFields);
      mergedDocumentTypes.push({
        ...documentType,
        Transport: new Transport({
          ...documentType.Transport,
          TransportFields: mergedTransportFields,
          CredentialsStatus: matchedSetupTransport?.CredentialsStatus
        })
      });
    });
    return mergedDocumentTypes;
  });

  mergeTransportFields(documentTransportFields: TransportField[], setupTransportFields: TransportField[]): TransportField[] {
    const mergedTransportFields: TransportField[] = [];
    setupTransportFields?.forEach(setupField => {
      const matchingDocumentField = documentTransportFields?.find(dtField => dtField.Name === setupField.Name);
      if (matchingDocumentField) {
        mergedTransportFields.push(matchingDocumentField);
      } else {
        mergedTransportFields.push(setupField);
      }
    });
    return mergedTransportFields;
  }

  getPartnerNameItem(partnerName: PartnerName): TextInformationItem {
    return {
      itemType: 'text',
      itemName: 'Company Name',
      itemValue: partnerName.Name,
      itemStatus: partnerName.Status,
      itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      partnerFilledItem: true
    };
  }

  getPartnerContactNameItem(partnerContact: PartnerContact): TextInformationItem {
    return {
      itemType: 'text',
      itemName: 'Contact name',
      itemValue: partnerContact.Name?.ContactName,
      itemStatus: partnerContact.Name?.Status,
      itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      partnerFilledItem: true
    };
  }

  getPartnerContactEmailItem(partnerContact: PartnerContact): TextInformationItem {
    return {
      itemType: 'text',
      itemName: 'Contact email',
      itemValue: partnerContact.Email?.ContactEmail,
      itemStatus: partnerContact.Email?.Status,
      itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      partnerFilledItem: true
    };
  }

  getPartnerContactPhoneNumberItem(partnerContact: PartnerContact): TextInformationItem {
    return {
      itemType: 'text',
      itemName: 'Phone number',
      itemValue: partnerContact.PhoneNumber?.PhoneNumber,
      itemStatus: partnerContact.PhoneNumber?.Status,
      itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      partnerFilledItem: true
    };
  }

  getIdentificationItem(identification: Identification): TextInformationItem {
    return {
      itemType: 'text',
      itemName: identification.IdentificationType?.Name,
      itemValue: identification.Value,
      itemStatus: identification.Status,
      itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      partnerFilledItem: true
    };
  }

  getDocumentTypeItem(documentType: DocumentTypeOutput): TextInformationItem {
    return {
      itemType: 'text',
      itemName: 'Format',
      itemValue: documentType.Format?.Name,
      itemStatus: documentType.Status,
      itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      partnerFilledItem: true
    };
  }

  getTransportFieldItem(transportField: TransportField, transport: Transport): TextInformationItem | PasswordInformationItem | CredentialsInformationItem | FileInformationItem | null {
    if (transportField.Type === TransportFieldType.Credentials) {
      return {
        itemType: 'credentials',
        itemName: transportField.Description,
        itemValue: this.parseCredentials(transportField.Value),
        itemStatus: null,
        partnerFilledItem: transportField.FilledBy === TransportFieldOwner.Partner,
        credentialsStatus: transport.CredentialsStatus,
        supportsProvideCredentials: transport.Type !== TransportType.IntegratedFTP && transport.Type !== TransportType.IntegratedSFTP,
        provideCredentialsClicked: (item: CredentialsInformationItem) => this.onProvideCredentials(item, transport.Type)
      };
    }
    else if (transportField.Type === TransportFieldType.Password) {
      return {
        itemType: 'password',
        itemName: transportField.Description,
        itemValue: this.parsePassword(transportField.Value),
        itemStatus: null,
        partnerFilledItem: transportField.FilledBy === TransportFieldOwner.Partner,
        itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      };
    }
    else if (transportField.Type === TransportFieldType.File) {
      return {
        itemType: 'file',
        itemName: transportField.Description,
        itemValue: this.parseOnboardingFile(transportField.Value),
        itemStatus: null,
        partnerFilledItem: transportField.FilledBy === TransportFieldOwner.Partner,
        itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      };
    }
    else if (transportField.Type === TransportFieldType.String) {
      return {
        itemType: 'text',
        itemName: transportField.Description,
        itemValue: transportField.Value,
        itemStatus: null,
        partnerFilledItem: transportField.FilledBy === TransportFieldOwner.Partner,
        itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      };
    }
    else if (transportField.Type === TransportFieldType.Dropdown) {
      return {
        itemType: 'text',
        itemName: transportField.Description,
        itemValue: transportField.Value,
        itemStatus: null,
        partnerFilledItem: transportField.FilledBy === TransportFieldOwner.Partner,
        itemUpdated: (event: ItemUpdatedEvent) => this.onItemUpdated(event),
      };
    }
    else return null;
  }

  getConversationData(conversationId: string, conversationOffset: string = '3rem', areaId: string = null): ConversationData | null {
    return {
      conversationId,
      areaId,
      visibleConversation: this.visibleConversation,
      autoRefreshEnabled: this.autoRefreshConversations,
      unreadConversationIds: this.unreadConversationIds,
      conversationContainerOffset: conversationOffset,
      toggleAutoRefreshClicked: () => this.onToggleAutoRefreshConversations(),
      toggleConversationClicked: (event: ToggleConversationEvent) => this.onToggleConversation(event),
      newCommentClicked: (event: NewCommentEvent) => this.onNewComment(event),
    };
  }

  parseCredentials(credentialsString: string): OnboardingCredentials | null {
      try {
          const credentials = JSON.parse(credentialsString) as OnboardingCredentials;
          return credentials;
      } catch {
          return null;
      }
  }

  parseOnboardingFile(fileString: string): OnboardingFile | null {
      try {
          const file = JSON.parse(fileString) as OnboardingFile;
          return file;
      } catch {
          return null;
      }
  }

  parsePassword(passwordString: string): PasswordComponentValue | null {
      if (!passwordString) return null;
      return {id: passwordString, value: null};
  }
    
  async onItemUpdated(event: ItemUpdatedEvent) : Promise<void> {
    console.log('Item updated:', event);
    return Promise.resolve();
  }

  async onProvideCredentials(item: CredentialsInformationItem, transportType: TransportType): Promise<void> {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    if (transportType === TransportType.IntegratedFTP || transportType === TransportType.IntegratedSFTP) {
      if (item.credentialsStatus === CredentialsStatus.Provided) {
        console.log('Credentials already provided for item:', item);
        await this.modalService.confirmDirect('Credentials already provided', 'The credentials for this transport have already been provided.', 'Ok')
      }
      else {
        console.log('Provide credentials clicked for item:', item);
      }
    }
    else {
      try {
        const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingProvideCredentialsResult, PartnerOnboardingProvideCredentialsDialogComponent>(PartnerOnboardingProvideCredentialsDialogComponent, modalSettings, dialog => {
          dialog.credentials.set(item.itemValue);
        });

        if (result.dialogResult === PartnerOnboardingProvideCredentialsDialogResult.Provide) {
          this.setup().SupportedTransports.forEach(transport => {
            if (transport?.Type === transportType) {
              const fieldIndex = transport.TransportFields.findIndex(f => f.Name === item.itemName);
              if (fieldIndex >= 0) {
                  transport.TransportFields[fieldIndex].Value = JSON.stringify(result.credentials);
                  if (item.itemType === 'credentials') {
                    transport.CredentialsStatus = result.credentials ? CredentialsStatus.Provided : CredentialsStatus.NotProvided;
                  }
              }
            }
          });

          await this.saveOnboarding().then(() => {
            this.refresh.emit();
          }).catch(error => {
            console.error('Error saving onboarding:', error);
          });
        }
      } catch { /* ignore dialog close */ }
    }
  }

  // Methods for accepting
  async acceptPartnerInformation(partnerName: PartnerName) {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    try {
      const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingAcceptDialogResult, PartnerOnboardingAcceptDialogComponent>(PartnerOnboardingAcceptDialogComponent, modalSettings, () => {});
      if (result === PartnerOnboardingAcceptDialogResult.AcceptAndCreate) {
        this.savePartnerName(partnerName).then(() => {
          partnerName.Status.Status = ItemStatus.Accepted;
          partnerName.Status.CreatedEntities = [];
          this.saveOnboarding().then(() => {
            this.refresh.emit();
          }).catch(error => {
            console.error('Error saving onboarding:', error);
          });
        }).catch(error => {
          console.error('Error saving partner name:', error);
        });
      }
      else if (result === PartnerOnboardingAcceptDialogResult.Accept) {
        partnerName.Status.Status = ItemStatus.Accepted;
        this.saveOnboarding().then(() => {
          this.refresh.emit();
        }).catch(error => {
          console.error('Error saving onboarding:', error);
        });
      }
    }
    catch {
      console.log('Dialog dismissed');
    }
  }

  async acceptContact(partnerContact: PartnerContact) {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    try {
      const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingAcceptContactResult, PartnerOnboardingAcceptContactDialogComponent>(PartnerOnboardingAcceptContactDialogComponent, modalSettings, dialog => {
        dialog.partnerContact.set(partnerContact);
      });

      if (result.dialogResult === PartnerOnboardingAcceptContactDialogResult.AcceptAndCreate) {
        partnerContact.Types = result.contactTypes;
        this.savePartnerContact(partnerContact).then((savedPartnerContactId) => {
          partnerContact.Name.Status.Status = ItemStatus.Accepted;
          partnerContact.Name.Status.CreatedEntities = this.addCreatedEntity(partnerContact.Name.Status.CreatedEntities, savedPartnerContactId, 'Contact');
          partnerContact.Email.Status.Status = ItemStatus.Accepted;
          partnerContact.Email.Status.CreatedEntities = [];
          partnerContact.PhoneNumber.Status.Status = ItemStatus.Accepted;
          partnerContact.PhoneNumber.Status.CreatedEntities = [];
          this.saveOnboarding().then(() => {
            this.refresh.emit();
          }).catch(error => {
            console.error('Error saving onboarding:', error);
          });
        }).catch(error => {
          console.error('Error saving partner name:', error);
        });
      }
      else if (result.dialogResult === PartnerOnboardingAcceptContactDialogResult.Accept) {
        partnerContact.Name.Status.Status = ItemStatus.Accepted;
        partnerContact.Email.Status.Status = ItemStatus.Accepted;
        partnerContact.PhoneNumber.Status.Status = ItemStatus.Accepted;
        this.saveOnboarding().then(() => {
          this.refresh.emit();
        }).catch(error => {
          console.error('Error saving onboarding:', error);
        });
      }
    }
    catch {
      console.log('Dialog dismissed');
    }
  }

  async acceptIdentification(identification: Identification) {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    try {
      const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingAcceptDialogResult, PartnerOnboardingAcceptDialogComponent>(PartnerOnboardingAcceptDialogComponent, modalSettings, () => {});
      if (result === PartnerOnboardingAcceptDialogResult.AcceptAndCreate) {
        this.saveIdentification(identification).then((savedIdentificationId) => {
          identification.Status.Status = ItemStatus.Accepted;
          identification.Status.CreatedEntities = this.addCreatedEntity(identification.Status.CreatedEntities, savedIdentificationId, 'Identification');
          this.saveOnboarding().then(() => {
            this.refresh.emit();
          }).catch(error => {
            console.error('Error saving onboarding:', error);
          });
        }).catch(error => {
          console.error('Error saving identification:', error);
        });
      }
      else if (result === PartnerOnboardingAcceptDialogResult.Accept) {
        identification.Status.Status = ItemStatus.Accepted;
        this.saveOnboarding().then(() => {
          this.refresh.emit();
        }).catch(error => {
          console.error('Error saving onboarding:', error);
        });
      }
      else {
        console.log('Dialog cancelled');
      }
    }
    catch {
      console.log('Dialog dismissed');
    }
  }

  async acceptDocumentType(documentType: DocumentTypeOutput, direction: string) {    
    if (documentType.Transport?.CredentialsStatus === CredentialsStatus.Requested) {
      await this.modalService.confirmDirect('Credentials requested', 'The partner has requested credentials for this transport. Please provide the credentials before accepting the document type.', 'OK', '');
      return;
    }
    try {
      const modalSettings = new ModalSettingsTemplate('lg', 'static');
      const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingAcceptDocumentTypeResult, PartnerOnboardingAcceptDocumenttypeDialogComponent>(PartnerOnboardingAcceptDocumenttypeDialogComponent, modalSettings, dialog => {
        dialog.direction.set(direction);
        dialog.documentType.set(documentType);
        dialog.onboardingPartnerId.set(this.partnerId());
        dialog.internalPartnerId.set(this.internalPartnerId());
      });
      if (result.dialogResult === PartnerOnboardingAcceptDocumentTypeDialogResult.AcceptAndCreate) {
        this.saveDocumentType(documentType, result.partnerInId, result.partnerOutId, result.documentConfigInId, result.documentConfigOutId, direction, result.transportOptions).then((savedDistributionId) => {
          documentType.Status.Status = ItemStatus.Accepted;
          documentType.Status.CreatedEntities = this.addCreatedEntity(documentType.Status.CreatedEntities, savedDistributionId, 'Distribution');
          this.saveOnboarding().then(() => {
            this.refresh.emit();
          }).catch(error => {
            console.error('Error saving onboarding:', error);
          });
        }).catch(error => {
          console.error('Error saving document type:', error);
        });
      }
      else if (result.dialogResult === PartnerOnboardingAcceptDocumentTypeDialogResult.Accept) {
        documentType.Status.Status = ItemStatus.Accepted;
        this.saveOnboarding().then(() => {
          this.refresh.emit();
        }).catch(error => {
          console.error('Error saving onboarding:', error);
        });
      }
      else {
        console.log('Dialog cancelled');
      }
    }
    catch {
      console.log('Dialog dismissed');
    }
  }


  // Methods for removal
  async removeIdentification(identification: Identification) {
    try {
      await this.modalService.confirmDirect('Discard partner information', 'This will discard the information provided by the partner', 'Discard information', 'Cancel');
      this.model().Identifications = this.model().Identifications?.filter(i => i !== identification);
      this.saveOnboarding().then(() => {
        this.refresh.emit();
      }).catch(error => {
        console.error('Error removing identification:', error);
      });
    }
    catch {
      console.log('Removal cancelled');
    }
  }

  async removeDocumentType(documentType: DocumentTypeOutput, direction: string) {
    try {
      await this.modalService.confirmDirect('Discard partner information', 'This will discard the information provided by the partner', 'Discard information', 'Cancel');
      if (direction === 'in') {
        this.model().DocumentTypesIn = this.model().DocumentTypesIn?.filter(i => i.Name !== documentType.Name);
      }
      else {
        this.model().DocumentTypesOut = this.model().DocumentTypesOut?.filter(i => i.Name !== documentType.Name);
      }
      this.saveOnboarding().then(() => {
        this.refresh.emit();
      }).catch(error => {
        console.error('Error removing document type:', error);
      });
    }
    catch {
      console.log('Removal cancelled');
    }
  }


  // Methods for reopening
  async reopenPartnerInformation(partnerName: PartnerName) {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingReopenDialogResult, PartnerOnboardingReopenDialogComponent>(PartnerOnboardingReopenDialogComponent, modalSettings, () => {});
    if (result.type === ReopenType.Reopen || result.type === ReopenType.ReopenAndNotify) {
      partnerName.Status.Status = ItemStatus.Open;

      this.saveOnboarding().then(() => {
        this.postActionComment([partnerName.ConversationId] , 'Field was reopened');
        if (result.type === ReopenType.ReopenAndNotify) {
          this.partnerOnboardingService.notifyPartnerFieldReopened(this.partnerOnboarding()?.Id, result.notificationMessage).then(() => {
          }).catch(error => {
            console.error('Error notifying partner about reopened field:', error);
          });
        }
        this.refresh.emit();
      }).catch(error => {
        console.error('Error reopening partner configuration fields:', error);
      });
    }
  }

  async reopenContact(partnerContact: PartnerContact) {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingReopenDialogResult, PartnerOnboardingReopenDialogComponent>(PartnerOnboardingReopenDialogComponent, modalSettings, () => {});
    if (result.type === ReopenType.Reopen || result.type === ReopenType.ReopenAndNotify) {
      partnerContact.Name.Status.Status = ItemStatus.Open;
      partnerContact.Email.Status.Status = ItemStatus.Open;
      partnerContact.PhoneNumber.Status.Status = ItemStatus.Open;

      this.saveOnboarding().then(() => {
        this.postActionComment([partnerContact.Name.ConversationId, partnerContact.Email.ConversationId, partnerContact.PhoneNumber.ConversationId] , 'Field was reopened');
        if (result.type === ReopenType.ReopenAndNotify) {
          this.partnerOnboardingService.notifyPartnerFieldReopened(this.partnerOnboarding()?.Id, result.notificationMessage).then(() => {
          }).catch(error => {
            console.error('Error notifying partner about reopened field:', error);
          });
        }
        this.refresh.emit();
      }).catch(error => {
        console.error('Error reopening partner contact fields:', error);
      });
    }
  }

  async reopenIdentification(identification: Identification) {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingReopenDialogResult, PartnerOnboardingReopenDialogComponent>(PartnerOnboardingReopenDialogComponent, modalSettings, () => {});
    if (result.type === ReopenType.Reopen || result.type === ReopenType.ReopenAndNotify) {
      identification.Status.Status = ItemStatus.Open;
      this.saveOnboarding().then(() => {
        this.postActionComment([identification.ConversationId], 'Field was reopened');
        if (result.type === ReopenType.ReopenAndNotify) {
          this.partnerOnboardingService.notifyPartnerFieldReopened(this.partnerOnboarding()?.Id, result.notificationMessage).then(() => {
          }).catch(error => {
            console.error('Error notifying partner about reopened field:', error);
          });
        }
        this.refresh.emit();
      }).catch(error => {
        console.error('Error reopening identification:', error);
      });
    }
  }

  async reopenDocumentType(documentType: DocumentTypeOutput) {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingReopenDialogResult, PartnerOnboardingReopenDialogComponent>(PartnerOnboardingReopenDialogComponent, modalSettings, () => {});
    if (result.type === ReopenType.Reopen || result.type === ReopenType.ReopenAndNotify) {
      documentType.Status.Status = ItemStatus.Open;
      this.saveOnboarding().then(() => {
        this.postActionComment([documentType.ConversationId], 'Field was reopened');
        if (result.type === ReopenType.ReopenAndNotify) {
          this.partnerOnboardingService.notifyPartnerFieldReopened(this.partnerOnboarding()?.Id, result.notificationMessage).then(() => {
          }).catch(error => {
            console.error('Error notifying partner about reopened field:', error);
          });
        }
        this.refresh.emit();
      }).catch(error => {
        console.error('Error reopening document type:', error);
      });
    }
  }


  // Navigation methods
  async goToDistribution(distributionId: number) {
    const url = '/distribution/result/details;distributionIds=' + distributionId;
    await this.router.navigateByUrl(url);
  }

  goToIdentification() {
    this.changeTab.emit('identifications');
  }

  goToOnboardingSetup() {
    this.changeTab.emit('onboarding');
  }

  goToContact() {
    this.changeTab.emit('contacts');
  }


  // Save onboarding data as Link entities
  async savePartnerName(name: PartnerName): Promise<PartnerName> {
    return this.partnerOnboardingService.savePartnerName(this.partnerId(), name);
  }

  async savePartnerContact(contact: PartnerContact): Promise<number> {
    return this.partnerOnboardingService.savePartnerContact(this.partnerId(), contact);
  }

  async saveIdentification(identification: Identification): Promise<number> {
    return this.partnerOnboardingService.saveIdentification(this.partnerId(), identification);
  }

  async saveDocumentType(documentType: DocumentTypeOutput, partnerInId: number, partnerOutId: number, documentConfigInId: number, documentConfigOutId: number, direction: string, options: Record<string, string>): Promise<number> {
    return this.partnerOnboardingService.saveDocumentType(partnerInId, partnerOutId, documentConfigInId, documentConfigOutId, documentType, direction.toLowerCase() === 'in', options);
  }


  // Save the entire onboarding data
  async saveOnboarding(): Promise<PartnerOnboarding> {
    const partnerOnboarding = this.partnerOnboarding();
    partnerOnboarding.Data.Output = this.model();
    const savedPartnerOnboarding = await this.partnerOnboardingService.savePartnerOnboarding(partnerOnboarding);
    return savedPartnerOnboarding;
  }


  // Mark onboarding as done
  async markOnboardingAsDone() {
    if (!this.canMarkOnboardingAsDone()) {
      console.error('Cannot mark onboarding as done, conditions not met');
      return;
    }
    try {
      await this.modalService.confirmDirect('Complete onboarding', 'This will mark the onboarding as completed. <br>If you at a later time require additional information from the partner, the onboarding can be reopened.<br><br>Do you want to proceed?', 'Complete onboarding', 'Cancel');
      this.partnerOnboarding().Status = PartnerOnboardingStatus.Done;
      this.saveOnboarding().then(async () => {

        await this.saveLogEvent('CustomerIntegrationDone');

        //TODO: Remove tag functionality when overview page is ready
        let playerTags = await this.partnerTagsService.getAllPartnerTags(this.selectedPartner.PartnersId);
        playerTags = playerTags.filter(tag => tag.Name.toLowerCase() !== 'onboarding');
        const playerTagsIds = playerTags.map(tag => tag.Id);
        if (playerTags) {
            void this.partnerTagsService.setAllPartnerTags(this.selectedPartner.PartnersId, playerTagsIds);
        }
        void this.productfruitsService.triggerEvent('Partner-Details/Onboarding-Done');
        this.refresh.emit();
      }).catch(error => {
        console.error('Error saving onboarding:', error);
      });
    }
    catch {
      console.log('Mark as done cancelled');
    }
  }


  // Reopen onboarding
  async reopenOnboarding() {
    if (!this.canReopenOnboarding()) {
      console.error('Cannot reopen onboarding, conditions not met');
      return;
    }
    try {
      await this.modalService.confirmDirect('Reopen onboarding', 'This will reopen the onboarding process. <br>The partner will be able to add new information but can not change any already accepted fields. To do so, you must reopen the individual fields afterwards. <br><br>Do you want to proceed?', 'Reopen onboarding', 'Cancel');
      this.partnerOnboarding().Status = PartnerOnboardingStatus.InProgress;
      this.saveOnboarding().then(() => {
        this.refresh.emit();
      }).catch(error => {
        console.error('Error saving onboarding:', error);
      });
    }
    catch {
      console.log('Reopen cancelled');
    }
  }


  // Post action comment in conversation
  postActionComment(conversationIds: string[], actionMessage: string) {
    const comment = new PartnerOnboardingComment({FromCustomer: true, IsAction: true, Message: actionMessage, Timestamp: new Date()});
    for (const convId of conversationIds) {
      this.partnerOnboardingService.postComment(comment, convId, this.partnerOnboarding()?.Id).then((conversation) => {
        if (conversation) {
          this.conversationResource.reload();
        }
      }).catch(error => {
        console.error('Error posting action comment:', error);
      });
    }
  }


  // Handlers for conversations
  async onToggleConversation(event: ToggleConversationEvent) {
    const conversationId = event.ConversationId;
    const areaId = event.AreaId;

    if (conversationId !== null) {
      const vc = new VissibleConversation(conversationId, areaId);

      this.vissibleConversationId.set(
          this.vissibleConversationId()?.Id === vc.Id &&
          this.vissibleConversationId()?.AreaId === vc.AreaId
              ? null
              : vc
      );

      if (this.vissibleConversationId() !== null) {
        await this.partnerOnboardingService.setConversationReadByCustomer(conversationId).then(() => {
          this.conversationResource.reload();
        });
      }
    }
  }

  onToggleAutoRefreshConversations() {
    this.autoRefreshConversations.set(!this.autoRefreshConversations());
  }

  async onNewComment(event: NewCommentEvent) {
    await this.partnerOnboardingService.postComment(event.Comment, event.ConversationId, this.partnerOnboarding()?.Id).then((conversation) => {
      if (conversation) {
        this.conversationResource.reload();
      }
    });
  }

  hasUnreadComments(conversation: PartnerConversation): boolean {
    if (!conversation.CustomerLastRead) return (conversation.Comments?.length ?? 0) > 0;
    return conversation.Comments?.some(c => !c.FromCustomer && c.Timestamp > conversation.CustomerLastRead);
  }


  // Helper methods
  async saveLogEvent(type: string) {
    const event = {
        Id: null,
        PartnerOnboardingId: this.partnerOnboarding().Id,
        Type: type} as PartnerOnboardingLogEvent;
    await this.partnerOnboardingService.logEvent(event);
  }

  supportsAcceptance(itemStatus: ItemStatus): boolean {
    const status = itemStatus?.toLowerCase();
    return (
      this.status() === PartnerOnboardingStatus.PartnerDone ||
      (this.status() === PartnerOnboardingStatus.InProgress && status === 'accepted')
    );
  }

  addCreatedEntity(createdEntities: CreatedEntity[] | null, id: number, type: string): CreatedEntity[] {
    const entityName = type + ' ' + id;
    const newEntity = new CreatedEntity( {Id: id, Name: entityName } );
    if (!createdEntities) {
      createdEntities = [];
    }
    if (createdEntities.findIndex(e => e.Id === newEntity.Id) < 0) {
      createdEntities.push(newEntity);
    };

    return createdEntities;
  }

  getTransportTypeDisplayName(transportType: TransportType): string {
    switch (transportType) {
      case TransportType.SFTPCustomerEndpoint:
        return 'SFTP';
      case TransportType.FTPCustomerEndpoint:
        return 'FTP';
      case TransportType.AS2:
        return 'AS2';
      case TransportType.FTPPartnerEndpoint:
        return 'FTP (Partner endpoint)';
      case TransportType.SFTPPartnerEndpoint:
        return 'SFTP (Partner endpoint)';
      case TransportType.IntegratedFTP:
      case TransportType.IntegratedSFTP:
        return 'Integrated FTP';
      default:
        return transportType;
    }
  }
}

class VissibleConversation {
    Id: string
    AreaId: string | null
    constructor(id: string, areaId: string | null) {
        this.Id = id;
        this.AreaId = areaId;
    }
}
